<template>
  <div>
    <PageHeaderBanner>
      <template #title>サプライヤ一覧</template>
    </PageHeaderBanner>
    <q-form autofocus greedy @submit="searchSupplier" @reset="clearSearchCondition">
      <div class="cmd00100-conditions-form">
        <q-card style="background: var(--q-lightGray)" flat bordered class="q-mb-lg">
          <q-card-section class="q-gutter-y-sm">
            <div class="row q-gutter-x-sm">
              <div class="col-2">
                <label>サプライヤコード</label>
                <q-input
                  v-model="supplierCode"
                  outlined
                  dense
                  style="background: var(--q-white)"
                  class="input-area"
                  maxlength="4"
                  @keydown.enter.prevent
                />
              </div>
              <div class="col">
                <label>サプライヤ名</label>
                <q-input
                  v-model="supplierName"
                  outlined
                  dense
                  style="background: var(--q-white)"
                  class="input-area"
                  maxlength="200"
                  @keydown.enter.prevent
                />
              </div>
            </div>
            <div class="row">
              <div class="col">
                <q-checkbox v-model="isAllSearch" label="無効データを含む" />
              </div>
              <div class="col q-gutter-x-xs flex justify-end" style="margin-top: 10px">
                <q-btn
                  type="submit"
                  color="primary"
                  style="width: 150px"
                  label="検索"
                  icon="mdi-magnify"
                />
                <q-btn
                  type="reset"
                  outline
                  style="
                    width: 150px;
                    background: var(--q-white) !important;
                    color: var(--q-delete);
                  "
                  icon="mdi-eraser"
                  label="クリア"
                />
              </div>
            </div>
          </q-card-section>
        </q-card>
      </div>
    </q-form>
    <!-- テーブル表示エリア -->
    <div class="cmd00100-result-table">
      <div
        :class="
          'chem-table-area btn-exist table-top-button ' + (rows.length === 0 ? 'table-nodata' : '')
        "
      >
        <q-table
          class="my-sticky-header-column-supplier-table"
          :rows="rows"
          :columns="columns"
          row-key="index"
          flat
          dense
          :visible-columns="visibleColumns"
          separator="cell"
          :hide-no-data="hideNoDataFlg"
          no-data-label="データがありません。"
          :rows-per-page-options="[10, 20, 50, 0]"
          rows-per-page-label="件表示"
          rows-per-page="20"
          :pagination-label="
            (firstRowIndex, endRowIndex, totalRowsNumber) => {
              return `${totalRowsNumber} 件中 ${firstRowIndex}～${endRowIndex} 件表示`;
            }
          "
        >
          <template #no-data="{ message }">
            <div class="full-width row q-gutter-sm">
              <q-icon size="24px" name="mdi-alert-circle-outline" />
              <span class="q-pt-xs">{{ message }}</span>
            </div>
          </template>
          <template #top>
            <div class="add-btn-area flex items-center filter-select">
              <q-select
                v-model="visibleColumns"
                multiple
                outlined
                dense
                options-dense
                display-value="カラム表示設定"
                emit-value
                map-options
                :options="visibleColumnOptions"
                option-value="name"
                color="primary"
                class="column-disp"
              />
              <q-btn
                icon="mdi-plus"
                label="追加"
                class="add-btn"
                color="primary"
                @click="registerData"
              />
            </div>
          </template>

          <!-- 編集Header -->
          <template #header-cell-edit>
            <q-th class="table-btn-area"></q-th>
          </template>
          <!-- 編集Body -->
          <template #body-cell-edit="cell">
            <q-td :class="'table-btn-area ' + (cell.row.del_flg === '1' ? 'deleted' : '')"
              ><div class="flex" style="justify-content: flex-start">
                <q-btn
                  icon="mdi-pencil"
                  label="編集"
                  color="primary"
                  @click="editData(cell.row.supplier_code)"
                />
              </div>
            </q-td>
          </template>
          <template #body-cell="cell">
            <q-td :props="cell" :class="cell.row.del_flg === '1' ? 'deleted' : ''">
              {{ cell.value }}
            </q-td>
          </template>
        </q-table>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { Loading } from 'quasar';
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import axios from '../../common/axios-common';

import ResultStatusCode from '../../common/code/ResultStatusCode';
import HttpResponseStatusCode from '../../common/code/HttpResponseStatusCode';
import PageHeaderBanner from '../../components/banner/PageHeaderBanner.vue';

interface MSupplier {
  supplier_code: string;
  supplier_name: string;
  priority: number;
  real_supplier_code: string;
  real_supplier_name: string;
  maker_code: string;
  del_flg: string;
  create_timestamp: Date;
  update_timestamp: Date;
  create_user_name: string;
  update_user_name: string;
}

// カラム表示機能
interface VisibleColumn {
  name: string;
  label: string;
}

const router = useRouter();

// 検索条件：サプライヤコード
const supplierCode = ref('');
// 検索条件：サプライヤ名
const supplierName = ref('');
// 検索条件：無効データを含む
const isAllSearch = ref(false);
// 表示カラム
const visibleColumns = ref<Array<string>>([]);
// カラム表示設定の選択肢
const visibleColumnOptions = ref<Array<VisibleColumn>>([]);
// 初期表示読み込み時「データがありません」を非表示にするためのフラグ(falseで表示)
const hideNoDataFlg = ref(true);

// カラム
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const columns: Array<any> = [
  {
    name: 'edit',
    field: (row: MSupplier) => row.supplier_code,
    required: true,
  },
  {
    name: 'supplier_code',
    label: 'サプライヤコード',
    align: 'left',
    field: (row: MSupplier) => row.supplier_code,
    sortable: true,
    style: 'width: 130px',
  },
  {
    name: 'supplier_name',
    label: 'サプライヤ名',
    align: 'left',
    field: (row: MSupplier) => row.supplier_name,
    sortable: true,
  },
  {
    name: 'priority',
    label: '優先順位',
    align: 'left',
    field: (row: MSupplier) => row.priority,
    classes: 'text-right',
    sortable: true,
    style: 'width: 80px',
  },
  {
    name: 'vendor_code',
    label: '仕入先コード',
    align: 'left',
    field: (row: MSupplier) => row.real_supplier_code,
    sortable: true,
    style: 'width: 110px',
  },
  {
    name: 'vendor_name',
    label: '仕入先名',
    align: 'left',
    field: (row: MSupplier) => row.real_supplier_name,
    sortable: true,
  },
  {
    name: 'maker_code',
    label: 'メーカーコード',
    align: 'left',
    field: (row: MSupplier) => row.maker_code,
    sortable: true,
    style: 'width: 120px',
  },
  {
    name: 'register',
    label: '登録者',
    align: 'left',
    field: (row: MSupplier) => row.create_user_name,
    sortable: true,
  },
  {
    name: 'regist_date',
    label: '登録日時',
    align: 'left',
    field: (row: MSupplier) => row.create_timestamp,
    sortable: true,
    style: 'width: 170px',
  },
  {
    name: 'updater',
    label: '更新者',
    align: 'left',
    field: (row: MSupplier) => row.update_user_name,
    sortable: true,
  },
  {
    name: 'update_date',
    label: '更新日時',
    align: 'left',
    field: (row: MSupplier) => row.update_timestamp,
    sortable: true,
    style: 'width: 170px',
  },
];

// 表示データ
const rows = ref<MSupplier[]>([]);

/**
 * サプライヤ新規登録に遷移
 */
const registerData = () => {
  router.push({ name: 'supplierRegist', params: { mode: '0' } });
};

/**
 * サプライヤ情報変更に遷移
 * @param {string} supplierCd サプライヤコード
 */
const editData = (supplierCd: string) => {
  router.push({ name: 'supplierEdit', params: { mode: '1', supplierCd } });
};

/**
 * サプライヤ検索
 */
const searchSupplier = async () => {
  // Loading Start
  Loading.show();
  // 検索条件設定
  const formData = new FormData();
  formData.append('supplier_code', supplierCode.value);
  formData.append('supplier_name', supplierName.value);
  formData.append('is_all_search', isAllSearch.value.toString());

  // サプライヤ検索
  await axios
    .post('/cmd001001/search', formData)
    .then((res) => {
      const { common, supplierList } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          rows.value = supplierList;
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          // エラーメッセージ表示
          router.push({ name: 'error' });
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
      hideNoDataFlg.value = false;
    });
};

/**
 * 検索条件クリア
 */
const clearSearchCondition = () => {
  supplierCode.value = '';
  supplierName.value = '';
  isAllSearch.value = false;
};

/**
 * 初期表示処理
 */
// カラム表示設定
for (let i = 0; i < columns.length; i += 1) {
  visibleColumns.value.push(columns[i].name);
  // 編集ボタンのカラムは除外
  if (columns[i].name !== 'edit') {
    visibleColumnOptions.value.push({
      name: columns[i].name,
      label: columns[i].label,
    });
  }
}
// サプライヤ検索
searchSupplier();
</script>
<style scoped>
.cmd00100-conditions-form {
  width: 70%;
  margin: 0 auto;
}
.cmd00100-result-table {
  width: 90%;
  margin: 0 auto;
}

.cmd00100-conditions-form .q-card {
  margin-bottom: 70px;
}
</style>

<style lang="sass">
.my-sticky-header-column-supplier-table
  /* height or max-height is important */
  max-height: 590px

  /* specifying max-width so the example can
    highlight the sticky column on any browser window */

  td:first-child
    /* bg color is important for td; just specify one */
    background-color: #fff

  tr th
    position: sticky
    /* higher than z-index for td below */
    z-index: 2
    /* bg color is important; just specify one */
    background: #fff

  /* this will be the loading indicator */
  thead tr:last-child th
    /* height of all previous header rows */
    top: 48px
    /* highest z-index */
    z-index: 3
  thead tr:first-child th
    top: 0
    z-index: 1
  tr:first-child th:first-child
    /* highest z-index */
    z-index: 3

  td:first-child
    z-index: 1

  td:first-child, th:first-child
    position: sticky
    left: 0

  /* prevent scrolling behind sticky top row on focus */
  tbody
    /* height of all previous header rows */
    scroll-margin-top: 48px
</style>
